import { Clock3, LoaderCircle, X } from 'lucide-react';
import { useState } from 'react';
import { Button, Input, Popover, PopoverContent, PopoverTrigger, cn } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';

interface TimeShiftControlProps {
  value: number | null;
  disabled: boolean;
  loading: boolean;
  error: boolean;
  onApply: (days: number) => void;
  onClear: () => void;
}

export function TimeShiftControl({
  value,
  disabled,
  loading,
  error,
  onApply,
  onClear,
}: TimeShiftControlProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => String(value ?? ''));
  const days = Number(draft);
  const hasDraft = draft.trim() !== '';
  const valid = hasDraft && Number.isSafeInteger(days) && days > 0;

  return (
    <div className="flex items-center gap-1.5">
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) setDraft(String(value ?? ''));
          setOpen(nextOpen);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={value === null ? 'outline' : 'default'}
            disabled={disabled}
            aria-invalid={error}
            className={cn(error && 'border-destructive text-destructive')}
          >
            {loading ? (
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Clock3 className="h-4 w-4" aria-hidden="true" />
            )}
            {value === null
              ? dashboardT('reportExplorer.chart.timeShift')
              : dashboardT('reportExplorer.chart.timeShiftActive', { days: value })}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-3">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">{dashboardT('reportExplorer.chart.timeShift')}</p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label={dashboardT('reportExplorer.chart.timeShiftClose')}
              onClick={() => {
                setOpen(false);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-start gap-2">
            <div className="relative min-w-0 flex-1">
              <Input
                type="number"
                min={1}
                step={1}
                value={draft}
                disabled={disabled}
                aria-label={dashboardT('reportExplorer.chart.timeShiftDays')}
                aria-invalid={hasDraft ? !valid : false}
                className="pr-12"
                onChange={(event) => {
                  setDraft(event.target.value);
                }}
              />
              <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs">
                {dashboardT('reportExplorer.chart.days')}
              </span>
            </div>
            <Button
              type="button"
              disabled={disabled || !valid || loading}
              onClick={() => {
                onApply(days);
                setOpen(false);
              }}
            >
              {dashboardT('reportExplorer.selection.apply')}
            </Button>
          </div>
          {hasDraft && !valid ? (
            <p className="text-destructive mt-2 text-xs" role="alert">
              {dashboardT('reportExplorer.chart.timeShiftValidation')}
            </p>
          ) : null}
          {error ? (
            <p className="text-destructive mt-2 text-xs" role="alert">
              {dashboardT('reportExplorer.chart.timeShiftError')}
            </p>
          ) : null}
        </PopoverContent>
      </Popover>
      {value !== null ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          className="text-destructive hover:text-destructive h-8 w-8 rounded-full"
          aria-label={dashboardT('reportExplorer.chart.timeShiftClear')}
          onClick={onClear}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
